// src/app/timesheets/services/admin-timesheet-overview.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, EMPTY, expand, map, reduce } from 'rxjs';
import { environment } from '../../../environments/environment';

import {
  ActiveEmployeeListItem,
  AdminWeeklyReportFilters,
  ApiCompanyListItem,
  ApiDepartmentListItem,
  ApiPagedResponse,
  ApiResponse,
  ApiWeeklyReportResponse,
  WeeklyReportBookedHoursDay,
  WeeklyReportDayLog,
  WeeklyReportDto,
} from '../models/admin-timesheet-overview.model';

@Injectable({ providedIn: 'root' })
export class AdminTimesheetOverviewService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private readonly http: HttpClient) {}

  getActiveEmployees(): Observable<ApiResponse<ActiveEmployeeListItem[]>> {
    return this.http.get<ApiResponse<ActiveEmployeeListItem[]>>(
      `${this.baseUrl}/api/users/active-employees`,
    );
  }

  getWeeklyReport(yyyyww: string): Observable<{ status: string; data: WeeklyReportDto }> {
    return this.getWeeklyReportByFilters({
      fullYearWeek: yyyyww,
    });
  }

  getWeeklyReportByFilters(
    filters: AdminWeeklyReportFilters,
  ): Observable<{ status: string; data: WeeklyReportDto }> {
    const params = this.buildWeeklyReportParams(filters);

    return this.http
      .get<ApiWeeklyReportResponse>(`${this.baseUrl}/api/employee-time-logs/weekly-report`, {
        params,
      })
      .pipe(map((res) => this.normalizeWeeklyReportResponse(res)));
  }

  private normalizeWeeklyReportResponse(
    res: ApiWeeklyReportResponse,
  ): { status: string; data: WeeklyReportDto } {
    if (res.status !== 'success' || !res.data) {
      return {
        status: 'error',
        data: this.emptyReport(),
      };
    }

    const api = res.data;

    const timeLogs = (api.timeLogs ?? []).map((item) => {
      const userWithLogs = item.user;
      const logs = userWithLogs.logs ?? [];
      const totalHours = this.sumClockedHours(logs);

      const { logs: _nestedLogs, ...user } = userWithLogs;

      return {
        user,
        logs,
        totalHours,
      };
    });

    const bookedhours = (api.bookedhours ?? []).map((item) => {
      const userWithBookedHours = item.user;

      const bookedHours: WeeklyReportBookedHoursDay[] = (
        userWithBookedHours.bookedHours ?? []
      ).map((day) => {
        const entries = day.bookedHours ?? day.bookedHour ?? [];

        return {
          date: this.toDateKey(day.date),
          bookedHours: entries,
          bookedHour: entries,
        };
      });

      const calculatedTotalHours = this.sumBookedHours(bookedHours);
      const totalHours =
        typeof item.totalHours === 'number' ? this.round2(item.totalHours) : calculatedTotalHours;

      const { bookedHours: _nestedBookedHours, ...user } = userWithBookedHours;

      return {
        user,
        bookedHours,
        totalHours,
      };
    });

    const userSchedules = (api.userSchedules ?? []).map((row) => {
      const { schedules: _nestedSchedules, ...user } = row.user;

      return {
        user,
        schedules: row.user.schedules ?? [],
      };
    });

    return {
      status: 'success',
      data: {
        payableHoursSync: api.payableHoursSync ?? null,
        timeLogs,
        bookedhours,
        userSchedules,
        invalidUserClockLogs: api.invalidUserClockLogs ?? [],
        dayOffRequests: api.dayOffRequests ?? [],
      },
    };
  }

  private emptyReport(): WeeklyReportDto {
    return {
      payableHoursSync: null,
      timeLogs: [],
      bookedhours: [],
      userSchedules: [],
      invalidUserClockLogs: [],
      dayOffRequests: [],
    };
  }

  private buildWeeklyReportParams(filters: AdminWeeklyReportFilters): HttpParams {
    let params = new HttpParams();

    const fullYearWeek = String(filters.fullYearWeek ?? '').trim();
    const searchTerm = String(filters.searchTerm ?? '').trim();

    if (fullYearWeek) {
      params = params.set('FullYearWeek', fullYearWeek);
    }

    if (filters.companyIds && filters.companyIds.length > 0) {
      params = params.set('companyIds', filters.companyIds.join(','));
    }

    if (filters.departmentIds && filters.departmentIds.length > 0) {
      params = params.set('departmentIds', filters.departmentIds.join(','));
    }

    if (typeof filters.employeeId === 'number') {
      params = params.set('employeeId', String(filters.employeeId));
    }

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    return params;
  }

  getDepartmentsPage(
    page: number,
    pageSize = 200,
    searchTerm = '',
  ): Observable<ApiPagedResponse<ApiDepartmentListItem>> {
    let params = new HttpParams()
      .set('pageNumber', String(page))
      .set('pageSize', String(pageSize));

    const term = String(searchTerm ?? '').trim();

    if (term) {
      params = params.set('searchTerm', term);
    }

    return this.http.get<ApiPagedResponse<ApiDepartmentListItem>>(
      `${this.baseUrl}/api/departments`,
      { params },
    );
  }

  getAllDepartments(pageSize = 200, searchTerm = ''): Observable<ApiDepartmentListItem[]> {
    return this.getAllPaged<ApiDepartmentListItem>((page) =>
      this.getDepartmentsPage(page, pageSize, searchTerm),
    ).pipe(
      map((items) =>
        [...items].sort((a, b) => {
          const byName = String(a.name ?? '').localeCompare(String(b.name ?? ''));

          return byName !== 0 ? byName : a.id - b.id;
        }),
      ),
    );
  }

  getCompaniesPage(
    page: number,
    pageSize = 200,
    searchTerm = '',
  ): Observable<ApiPagedResponse<ApiCompanyListItem>> {
    let params = new HttpParams()
      .set('pageNumber', String(page))
      .set('pageSize', String(pageSize));

    const term = String(searchTerm ?? '').trim();

    if (term) {
      params = params.set('searchTerm', term);
    }

    return this.http.get<ApiPagedResponse<ApiCompanyListItem>>(`${this.baseUrl}/api/companies`, {
      params,
    });
  }

  getAllCompanies(pageSize = 200, searchTerm = ''): Observable<ApiCompanyListItem[]> {
    return this.getAllPaged<ApiCompanyListItem>((page) =>
      this.getCompaniesPage(page, pageSize, searchTerm),
    ).pipe(
      map((items) =>
        [...items].sort((a, b) => {
          const byName = String(a.name ?? '').localeCompare(String(b.name ?? ''));

          return byName !== 0 ? byName : a.id - b.id;
        }),
      ),
    );
  }

  private getAllPaged<T>(
    fetchPage: (page: number) => Observable<ApiPagedResponse<T>>,
  ): Observable<T[]> {
    return fetchPage(1).pipe(
      expand((res: ApiPagedResponse<T>) => {
        const nextPage = (res?.currentPage ?? 1) + 1;
        const totalPages = res?.totalPages ?? 1;

        return nextPage <= totalPages ? fetchPage(nextPage) : EMPTY;
      }),
      reduce((acc: T[], res: ApiPagedResponse<T>) => {
        if (res?.status === 'success') {
          acc.push(...(res.data ?? []));
        }

        return acc;
      }, []),
    );
  }

  private sumClockedHours(days: WeeklyReportDayLog[]): number {
    let total = 0;

    for (const day of days ?? []) {
      for (const log of day.timeLogs ?? []) {
        total += Number(log?.totalHours ?? log?.hours ?? 0) || 0;
      }
    }

    return this.round2(total);
  }

  private sumBookedHours(days: WeeklyReportBookedHoursDay[]): number {
    let total = 0;

    for (const day of days ?? []) {
      const entries = day.bookedHours ?? day.bookedHour ?? [];

      for (const bookedHour of entries) {
        total += Number(bookedHour?.hours ?? 0) || 0;
      }
    }

    return this.round2(total);
  }

  private toDateKey(value: string | null | undefined): string {
    return String(value ?? '').slice(0, 10);
  }

  private round2(value: number): number {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  }
}